// Controls per platform, and the devices that drive them: keyboard (remappable, per-platform defaults), gamepads
// (standard mapping). Touch controls live in pages/touch.tsx and use the same control ids. Every source produces a
// PadState with active-high bits (spec §4.6); sources for the same port are merged.
import { GBA_KEYS, N64_BUTTONS } from '../../shared/manifest.ts';
import type { PadState } from '../../shared/protocol.ts';
import { loadPref, savePref, deletePref } from './prefs.ts';

export interface Extra { bit: number; label: string }
/** One thing the player can press. `bits` go into rtw_input's buttons; `stick` adds to the analog stick. */
export interface Control { id: string; label: string; bits: number; stick?: [number, number] }

const g = (k: keyof typeof GBA_KEYS) => 1 << GBA_KEYS[k];

const GBA_CONTROLS: Control[] = [
  { id: 'Up', label: 'Up', bits: g('Up') }, { id: 'Down', label: 'Down', bits: g('Down') },
  { id: 'Left', label: 'Left', bits: g('Left') }, { id: 'Right', label: 'Right', bits: g('Right') },
  { id: 'A', label: 'A', bits: g('A') }, { id: 'B', label: 'B', bits: g('B') },
  { id: 'L', label: 'L', bits: g('L') }, { id: 'R', label: 'R', bits: g('R') },
  { id: 'Start', label: 'Start', bits: g('Start') }, { id: 'Select', label: 'Select', bits: g('Select') },
];

const N64_CONTROLS: Control[] = [
  { id: 'StickUp', label: 'Stick up', bits: 0, stick: [0, 127] }, { id: 'StickDown', label: 'Stick down', bits: 0, stick: [0, -127] },
  { id: 'StickLeft', label: 'Stick left', bits: 0, stick: [-127, 0] }, { id: 'StickRight', label: 'Stick right', bits: 0, stick: [127, 0] },
  { id: 'A', label: 'A', bits: N64_BUTTONS.A }, { id: 'B', label: 'B', bits: N64_BUTTONS.B },
  { id: 'Z', label: 'Z', bits: N64_BUTTONS.Z }, { id: 'Start', label: 'Start', bits: N64_BUTTONS.Start },
  { id: 'L', label: 'L', bits: N64_BUTTONS.L }, { id: 'R', label: 'R', bits: N64_BUTTONS.R },
  { id: 'CUp', label: 'C up', bits: N64_BUTTONS.CUp }, { id: 'CDown', label: 'C down', bits: N64_BUTTONS.CDown },
  { id: 'CLeft', label: 'C left', bits: N64_BUTTONS.CLeft }, { id: 'CRight', label: 'C right', bits: N64_BUTTONS.CRight },
  { id: 'DUp', label: 'D-pad up', bits: N64_BUTTONS.DUp }, { id: 'DDown', label: 'D-pad down', bits: N64_BUTTONS.DDown },
  { id: 'DLeft', label: 'D-pad left', bits: N64_BUTTONS.DLeft }, { id: 'DRight', label: 'D-pad right', bits: N64_BUTTONS.DRight },
];

export const extraId = (bit: number) => `x${bit}`;

export function controlsFor(platform: string, extra: Extra[] = []): Control[] {
  const base = platform === 'n64' ? N64_CONTROLS : GBA_CONTROLS;
  return [...base, ...extra.filter((x) => x.bit >= 16 && x.bit <= 31).map((x) => ({ id: extraId(x.bit), label: x.label, bits: 2 ** x.bit | 0 }))];
}

/** Key code -> control id. */
export type Keymap = Record<string, string>;

const GBA_KEYS_DEFAULT: Keymap = {
  ArrowUp: 'Up', ArrowDown: 'Down', ArrowLeft: 'Left', ArrowRight: 'Right',
  KeyX: 'A', KeyZ: 'B', KeyA: 'L', KeyS: 'R', Enter: 'Start', Backspace: 'Select', ShiftRight: 'Select',
};
const N64_KEYS_DEFAULT: Keymap = {
  ArrowUp: 'StickUp', ArrowDown: 'StickDown', ArrowLeft: 'StickLeft', ArrowRight: 'StickRight',
  KeyX: 'A', KeyC: 'B', KeyZ: 'Z', Enter: 'Start', KeyA: 'L', KeyS: 'R',
  KeyI: 'CUp', KeyK: 'CDown', KeyJ: 'CLeft', KeyL: 'CRight',
  KeyT: 'DUp', KeyG: 'DDown', KeyF: 'DLeft', KeyH: 'DRight',
};

export function defaultKeymap(platform: string, extra: Extra[] = []): Keymap {
  const map: Keymap = { ...(platform === 'n64' ? N64_KEYS_DEFAULT : GBA_KEYS_DEFAULT) };
  extra.slice(0, 9).forEach((x, i) => { map[`Digit${i + 1}`] = extraId(x.bit); });
  return map;
}

const keymapPref = (platform: string) => `rtw:keymap:${platform}`;

/** The player's key map for a platform (stored per browser), with defaults for controls it doesn't bind. */
export function loadKeymap(platform: string, extra: Extra[] = []): Keymap {
  const ids = new Set(controlsFor(platform, extra).map((c) => c.id));
  const stored = loadPref<Keymap | null>(keymapPref(platform), null);
  if (!stored || typeof stored !== 'object') return defaultKeymap(platform, extra);
  const map: Keymap = {};
  for (const [code, id] of Object.entries(stored)) if (typeof id === 'string' && (ids.has(id) || /^x\d+$/.test(id))) map[code] = id;
  // controls with no key yet (e.g. this game's extra buttons) get their default key if it's free
  const bound = new Set(Object.values(map));
  for (const [code, id] of Object.entries(defaultKeymap(platform, extra))) if (!bound.has(id) && !(code in map)) map[code] = id;
  return map;
}

export function saveKeymap(platform: string, map: Keymap): void { savePref(keymapPref(platform), map); }
export function resetKeymap(platform: string): void { deletePref(keymapPref(platform)); }

/** Bind `code` to control `id` (one key per control: other keys for that control are removed). */
export function rebind(map: Keymap, id: string, code: string): Keymap {
  const out: Keymap = {};
  for (const [c, v] of Object.entries(map)) if (v !== id && c !== code) out[c] = v;
  out[code] = id;
  return out;
}

const clamp = (v: number) => Math.max(-127, Math.min(127, Math.round(v)));

export function stateFrom(controls: Control[], pressed: Iterable<string>): PadState {
  const byId = new Map(controls.map((c) => [c.id, c]));
  let buttons = 0, sx = 0, sy = 0;
  for (const id of pressed) {
    const c = byId.get(id);
    if (!c) continue;
    buttons |= c.bits;
    if (c.stick) { sx += c.stick[0]; sy += c.stick[1]; }
  }
  return { buttons: buttons | 0, sx: clamp(sx), sy: clamp(sy) };
}

/** Buttons OR-ed, sticks added and clamped. */
export function mergePads(...pads: PadState[]): PadState {
  let buttons = 0, sx = 0, sy = 0, rx = 0, ry = 0;
  for (const p of pads) { buttons |= p.buttons; sx += p.sx; sy += p.sy; rx += p.rx ?? 0; ry += p.ry ?? 0; }
  return { buttons: buttons | 0, sx: clamp(sx), sy: clamp(sy), rx: clamp(rx), ry: clamp(ry) };
}

export const NO_INPUT: PadState = { buttons: 0, sx: 0, sy: 0 };

export class KeyboardPad {
  private down = new Set<string>();
  private readonly controls: Control[];
  map: Keymap;
  readonly platform: string;
  constructor(platform: string, extra: Extra[] = [], map?: Keymap) {
    this.platform = platform;
    this.controls = controlsFor(platform, extra);
    this.map = map ?? defaultKeymap(platform, extra);
  }
  /** Returns true if the key is one we use. */
  set(code: string, isDown: boolean): boolean {
    if (!(code in this.map)) return false;
    if (isDown) this.down.add(code); else this.down.delete(code);
    return true;
  }
  handles(code: string): boolean { return code in this.map; }
  clear() { this.down.clear(); }
  state(): PadState {
    return stateFrom(this.controls, [...this.down].map((c) => this.map[c]));
  }
}

// ---- Gamepad API, standard mapping (https://w3c.github.io/gamepad/#remapping) ----------------------------------
// 0 south, 1 east, 2 west, 3 north, 4 LB, 5 RB, 6 LT, 7 RT, 8 back, 9 start, 10 LS, 11 RS, 12-15 d-pad U/D/L/R;
// axes 0/1 left stick, 2/3 right stick (down = +y).

const GBA_PAD: Record<number, string> = {
  1: 'A', 0: 'B', 4: 'L', 6: 'L', 5: 'R', 7: 'R', 8: 'Select', 9: 'Start', 12: 'Up', 13: 'Down', 14: 'Left', 15: 'Right',
};
const N64_PAD: Record<number, string> = {
  0: 'A', 2: 'B', 1: 'CDown', 3: 'CLeft', 6: 'Z', 4: 'L', 5: 'R', 7: 'R', 9: 'Start', 12: 'DUp', 13: 'DDown', 14: 'DLeft', 15: 'DRight',
};
/** Buttons left free by the platform layout, used for the manifest's extra buttons in order. */
const EXTRA_PAD: Record<string, number[]> = { gba: [2, 3, 10, 11], n64: [8, 10, 11] };

export interface GamepadLike {
  mapping: string;
  buttons: readonly { pressed: boolean; value: number }[];
  axes: readonly number[];
}

const DEAD = 0.15;

export function gamepadState(gp: GamepadLike, platform: string, extra: Extra[] = []): PadState {
  const controls = controlsFor(platform, extra);
  const map = platform === 'n64' ? N64_PAD : GBA_PAD;
  const pressed: string[] = [];
  const btn = (i: number) => { const b = gp.buttons[i]; return !!b && (b.pressed || b.value > 0.5); };
  for (const [i, id] of Object.entries(map)) if (btn(Number(i))) pressed.push(id);
  (EXTRA_PAD[platform] ?? []).forEach((i, k) => { if (extra[k] && btn(i)) pressed.push(extraId(extra[k].bit)); });
  const ax = (i: number) => { const v = gp.axes[i] ?? 0; return Math.abs(v) < DEAD ? 0 : v; };
  const s = stateFrom(controls, pressed);
  // v1.1: the right stick also goes out as analog values (rtw_rstick); N64 keeps the C buttons too
  s.rx = clamp(ax(2) * 127) | 0;
  s.ry = clamp(-ax(3) * 127) | 0;
  if (platform === 'n64') {
    s.sx = clamp(s.sx + ax(0) * 127);
    s.sy = clamp(s.sy - ax(1) * 127);
    const rx = ax(2), ry = ax(3);
    if (rx > 0.5) s.buttons |= N64_BUTTONS.CRight;
    if (rx < -0.5) s.buttons |= N64_BUTTONS.CLeft;
    if (ry > 0.5) s.buttons |= N64_BUTTONS.CDown;
    if (ry < -0.5) s.buttons |= N64_BUTTONS.CUp;
  } else {
    const x = ax(0), y = ax(1);
    if (x > 0.5) s.buttons |= g('Right');
    if (x < -0.5) s.buttons |= g('Left');
    if (y > 0.5) s.buttons |= g('Down');
    if (y < -0.5) s.buttons |= g('Up');
  }
  return s;
}
